import { memo } from 'react';
import { styled } from '@mui/material/styles';
import Rating from '@mui/material/Rating';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { Box, Typography } from '@mui/material';

const StyledRating = styled(Rating)(({ theme }) => ({
  '& .MuiRating-iconEmpty .MuiSvgIcon-root': {
    color: theme.palette.action.disabled,
  },
}));

// Color gradient from red (1) to green (10)
const ratingColors: Record<number, string> = {
  1: '#FF203A',
  2: '#FF543B',
  3: '#FF8139',
  4: '#FFA638',
  5: '#FFC61F',
  6: '#E3CB24',
  7: '#B7D229',
  8: '#8CD733',
  9: '#08CA0A',
  10: '#00964E',
};

const ratingLabels: Record<number, string> = {
  1: 'Very Dissatisfied',
  2: 'Very Dissatisfied',
  3: 'Dissatisfied',
  4: 'Dissatisfied',
  5: 'Neutral',
  6: 'Neutral',
  7: 'Satisfied',
  8: 'Satisfied',
  9: 'Very Satisfied',
  10: 'Very Satisfied',
};

interface IconContainerProps extends React.HTMLAttributes<HTMLSpanElement> {
  value: number;
}

const IconContainer = ({ value, ...other }: IconContainerProps) => (
  <span {...other}>
    <StarIcon fontSize="inherit" sx={{ color: ratingColors[value] }} />
  </span>
);

interface SurveyStarRatingProps {
  value: number | null;
  onChange: (value: number | null) => void;
  disabled?: boolean;
}

const SurveyStarRating = memo(function SurveyStarRating({
  value,
  onChange,
  disabled = false,
}: SurveyStarRatingProps) {
  return (
    <Box sx={{ width: '100%' }}>
      <StyledRating
        value={value}
        max={10}
        emptyIcon={<StarBorderIcon fontSize="inherit" />}
        getLabelText={(val) => ratingLabels[val] || ''}
        highlightSelectedOnly={false}
        disabled={disabled}
        slotProps={{
          icon: {
            component: IconContainer,
          },
        }}
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          // iPad-optimized sizing
          fontSize: { xs: '2.2rem', sm: '3.5rem', md: '4rem', lg: '3.5rem' },
          '& .MuiRating-icon': {
            transition: 'transform 0.2s ease-in-out',
          },
          '& .MuiRating-icon:hover': {
            transform: 'scale(1.15)',
          },
          '& .MuiRating-icon:active': {
            transform: 'scale(1.05)',
          },
        }}
        onChange={(_, newValue) => {
          onChange(newValue);
        }}
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          fontWeight: 500,
          mt: { xs: 1, sm: 1.5, md: 2 },
          width: '100%',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: {
              xs: '0.6rem',
              sm: '0.75rem',
              md: '0.85rem',
              lg: '0.8rem',
            },
            color: 'text.secondary',
            textAlign: 'left',
          }}
        >
          Very Dissatisfied
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontSize: {
              xs: '0.6rem',
              sm: '0.75rem',
              md: '0.85rem',
              lg: '0.8rem',
            },
            color: 'text.secondary',
            textAlign: 'center',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          Dissatisfied
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontSize: {
              xs: '0.6rem',
              sm: '0.75rem',
              md: '0.85rem',
              lg: '0.8rem',
            },
            color: 'text.secondary',
            textAlign: 'center',
          }}
        >
          Neutral
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontSize: {
              xs: '0.6rem',
              sm: '0.75rem',
              md: '0.85rem',
              lg: '0.8rem',
            },
            color: 'text.secondary',
            textAlign: 'center',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          Satisfied
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontSize: {
              xs: '0.6rem',
              sm: '0.75rem',
              md: '0.85rem',
              lg: '0.8rem',
            },
            color: 'text.secondary',
            textAlign: 'right',
          }}
        >
          Very Satisfied
        </Typography>
      </Box>
    </Box>
  );
});

export default SurveyStarRating;
